"use server";

import { redirect } from "next/navigation";
import { readClientIp } from "@/lib/auth/client-ip";
import { clearSession, getSession, setSession } from "@/lib/auth/cookies";
import { verifyPassword } from "@/lib/auth/password";
import type { SessionRole } from "@/lib/auth/types";
import {
  endOpenUserSessions,
  endUserSession,
  startUserSession,
} from "@/lib/auth/user-session";
import { unscopedPrisma } from "@/lib/db";
import { platformCopy } from "@/lib/ui/platform";

export type LoginState = {
  error: string | null;
};

export async function loginAction(
  _prev: LoginState,
  formData: FormData,
): Promise<LoginState> {
  const email = String(formData.get("email") ?? "")
    .trim()
    .toLowerCase();
  const password = String(formData.get("password") ?? "");

  if (!email || !password) {
    return { error: "Введите email и пароль" };
  }

  const user = await unscopedPrisma.user.findUnique({
    where: { email },
    include: { roles: true, tenant: { select: { suspendedAt: true } } },
  });

  if (!user || !(await verifyPassword(password, user.passwordHash))) {
    return { error: "Неверный email или пароль" };
  }

  if (user.blockedAt) {
    return { error: platformCopy.userBlockedLogin };
  }

  if (user.tenant.suspendedAt) {
    return { error: platformCopy.tenantSuspendedLogin };
  }

  const previous = await getSession();
  if (previous?.sessionId) {
    await endUserSession(previous.sessionId);
  }

  const sessionId = await startUserSession({
    userId: user.id,
    ip: await readClientIp(),
  });

  await setSession({
    userId: user.id,
    tenantId: user.tenantId,
    email: user.email,
    name: user.name,
    roles: user.roles.map((item) => item.role as SessionRole),
    sessionId,
  });

  redirect("/");
}

export async function logoutAction(): Promise<void> {
  const session = await getSession();
  if (session?.sessionId) {
    await endUserSession(session.sessionId);
  } else if (session) {
    await endOpenUserSessions(session.userId);
  }
  await clearSession();
  redirect("/login");
}
